import { useState, useEffect, useCallback } from 'react';
import { coreApi } from '@shared/api/client';
import { Relationship, CreateRelationshipInput, Contact, Company } from '../types';

const INITIAL_FALLBACK_RELATIONSHIPS: Relationship[] = [
  {
    id: 'rel1',
    contact_id: 'c1',
    contact_name: 'Sarah Connor',
    company_id: 'comp1',
    company_name: 'Acme Corporation',
    role: 'VP of Procurement',
    is_primary: true,
    created_at: new Date().toISOString(),
  },
  {
    id: 'rel2',
    contact_id: 'c2',
    contact_name: 'John Doe',
    company_id: 'comp1',
    company_name: 'Acme Corporation',
    role: 'Chief Technology Officer',
    is_primary: true,
    created_at: new Date().toISOString(),
  },
];

export function useRelationships() {
  const [relationships, setRelationships] = useState<Relationship[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [relsData, contactsData, companiesData] = await Promise.all([
        coreApi.get<Relationship[]>('/relationships').catch(() => null),
        coreApi.get<Contact[]>('/contacts').catch(() => null),
        coreApi.get<Company[]>('/companies').catch(() => null),
      ]);

      const rawContacts = Array.isArray(contactsData) ? contactsData : [];
      const rawCompanies = Array.isArray(companiesData) ? companiesData : [];
      setContacts(rawContacts);
      setCompanies(rawCompanies);

      const contactMap = new Map(rawContacts.map((c) => [c.id, `${c.first_name} ${c.last_name}`.trim() || c.email]));
      const companyMap = new Map(rawCompanies.map((co) => [co.id, co.name]));

      const rawRels = Array.isArray(relsData) ? relsData : INITIAL_FALLBACK_RELATIONSHIPS;

      // Ensure names are 100% resolved
      const resolved = rawRels.map((r) => ({
        ...r,
        contact_name: r.contact_name || contactMap.get(r.contact_id) || 'Individual (' + r.contact_id + ')',
        company_name: r.company_name || companyMap.get(r.company_id) || 'Company (' + r.company_id + ')',
      }));

      setRelationships(resolved);
    } catch {
      setRelationships(INITIAL_FALLBACK_RELATIONSHIPS);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const createRelationship = async (input: CreateRelationshipInput) => {
    const contact = contacts.find((c) => c.id === input.contact_id);
    const company = companies.find((co) => co.id === input.company_id);
    const resolvedInput = {
      ...input,
      contact_name: input.contact_name || (contact ? `${contact.first_name} ${contact.last_name}`.trim() : undefined),
      company_name: input.company_name || company?.name,
    };

    try {
      const created = await coreApi.post<Relationship>('/relationships', resolvedInput);
      setRelationships((prev) => [created || { ...resolvedInput, id: `rel-${Date.now()}` }, ...prev]);
    } catch {
      const local: Relationship = {
        ...resolvedInput,
        id: `rel-${Date.now()}`,
        created_at: new Date().toISOString(),
      };
      setRelationships((prev) => [local, ...prev]);
    }
  };

  const updateRelationship = async (id: string, input: Partial<Relationship>) => {
    try {
      const updated = await coreApi.put<Relationship>(`/relationships/${id}`, input);
      setRelationships((prev) => prev.map((r) => (r.id === id ? { ...r, ...updated, ...input } : r)));
    } catch {
      setRelationships((prev) => prev.map((r) => (r.id === id ? { ...r, ...input } : r)));
    }
  };

  const deleteRelationship = async (id: string) => {
    try {
      await coreApi.delete(`/relationships/${id}`);
    } catch {
      // ignore
    }
    setRelationships((prev) => prev.filter((r) => r.id !== id));
  };

  return {
    relationships,
    contacts,
    companies,
    isLoading,
    error,
    createRelationship,
    updateRelationship,
    deleteRelationship,
    reload: fetchAll,
  };
}

export default useRelationships;
